import {
    EmployeeRequestService,
    type EmployeeRequest,
    type Status,
} from '@api/EmployeeRequestService';
import { useCallback, useState } from 'react';
import { invalidateOrgEmployeeRequestData } from '@stores/orgEmployeeRequestStore';

interface UseEmployeeRequestStatusUpdateResult {
    updatedEmployeeRequest: EmployeeRequest | undefined;
    isEmployeeRequestStatusUpdateInProgress: boolean;
    updateEmployeeRequestStatus: (
        requestId: string,
        status: Status,
        comment?: string,
    ) => void;
    error?: string | undefined;
}

// TODO: Use useApiCall hook
const useEmployeeRequestStatusUpdate = (
    departmentId: string | undefined,
    employeeId: string | undefined,
): UseEmployeeRequestStatusUpdateResult => {
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [updatedEmployeeRequest, setUpdatedEmployeeRequest] =
        useState<EmployeeRequest>();
    const [error, setError] = useState<string>();

    /*
     * This effect handles the sequential API calls:
     * 1. First put the comment (if it exists)
     * 2. Then update the status
     */
    const performStatusUpdate = useCallback(
        async (requestId: string, status: Status, comment?: string) => {
            if (!departmentId || !employeeId) {
                return;
            }

            setIsUpdating(true);
            setError(undefined);

            try {
                // Step 1: Put comment if it exists
                if (comment) {
                    await EmployeeRequestService.putEmployeeRequestComment(
                        departmentId,
                        employeeId,
                        requestId,
                        { value: comment },
                    );
                }

                // Step 2: Update status
                const response =
                    await EmployeeRequestService.putEmployeeRequestStatus(
                        departmentId,
                        employeeId,
                        requestId,
                        { value: status },
                    );
                setUpdatedEmployeeRequest(response);

                // The mutation changed request data — drop cached reads
                invalidateOrgEmployeeRequestData();
            } catch (err: unknown) {
                setError(
                    (err as Error)?.message ??
                        'Something went wrong while executing the operation.',
                );
            } finally {
                setIsUpdating(false);
            }
        },
        [departmentId, employeeId],
    );

    const updateEmployeeRequestStatus = useCallback(
        (requestId: string, status: Status, comment?: string) => {
            performStatusUpdate(requestId, status, comment);
        },
        [performStatusUpdate],
    );

    return {
        updatedEmployeeRequest,
        isEmployeeRequestStatusUpdateInProgress: isUpdating,
        updateEmployeeRequestStatus,
        error,
    };
};

export default useEmployeeRequestStatusUpdate;
